import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, addDoc, onSnapshot, query, doc, updateDoc, deleteDoc, orderBy, serverTimestamp, where, getDocs, writeBatch, setDoc } from 'firebase/firestore';
import toast, { Toaster } from 'react-hot-toast';
import Modal from 'react-modal';
import { FaPlus, FaEdit, FaTrash, FaFilePdf, FaTruckLoading, FaCheckCircle, FaShoppingCart, FaUndo, FaTools, FaCashRegister } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import CreatableSelect from 'react-select/creatable';
import { PDFDownloadLink } from '@react-pdf/renderer';
import OrdenCompraPDF from './components/OrdenCompraPDF';
import RequerimientoMadera from './components/RequerimientoMadera';
import RequerimientoSoftplay from './components/RequerimientoSoftplay';

const customModalStyles = {
    content: {
        top: '50%', left: '50%', right: 'auto', bottom: 'auto',
        marginRight: '-50%', transform: 'translate(-50%, -50%)',
        border: 'none', background: 'transparent', width: '90%', maxWidth: '800px', padding: '0'
    },
    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 1000 }
};

Modal.setAppElement('#root');

// --- Chad: Formulario del Modal adaptado ---
function OrdenCompraForm({ onSave, onCancel, initialData, isDirectPurchase, proveedoresOptions, inventarioOptions }) {
    const [proveedor, setProveedor] = useState(null);
    const [fecha, setFecha] = useState(new Date());
    const [currentItem, setCurrentItem] = useState({ producto: null, cantidad: 1, costoUnitario: 0, categoria: 'materiaPrima', isNew: false });
    const [items, setItems] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    
    useEffect(() => {
        if (initialData) {
            setProveedor(initialData.proveedor ? { label: initialData.proveedor, value: initialData.proveedor } : null);
            setFecha(initialData.fecha?.toDate ? initialData.fecha.toDate() : (initialData.fecha || new Date()));
            setItems(initialData.items || []);
        } else {
            setProveedor(null); setFecha(new Date()); setItems([]); 
            setCurrentItem({ producto: null, cantidad: 1, costoUnitario: 0, categoria: 'materiaPrima', isNew: false });
        }
        setEditingIndex(null);
    }, [initialData]);

    const handleProductChange = (selectedOption, actionMeta) => {
        if (actionMeta.action === 'create-option') {
            setCurrentItem(prev => ({ ...prev, producto: selectedOption, isNew: true }));
        } else {
            setCurrentItem(prev => ({ ...prev, producto: selectedOption, categoria: selectedOption?.categoria || 'materiaPrima', isNew: false }));
        }
    };
    
    const handleAddItem = () => {
        if (!currentItem.producto?.value || currentItem.cantidad <= 0 || currentItem.costoUnitario < 0) {
            return toast.error("Producto, cantidad y costo son obligatorios.");
        }
        const newItem = { 
            producto: currentItem.producto.value, 
            cantidad: currentItem.cantidad, 
            costoUnitario: currentItem.costoUnitario,
            costoTotalItem: currentItem.cantidad * currentItem.costoUnitario,
            categoria: currentItem.categoria
        };
        setItems([...items, newItem]);
        setCurrentItem({ producto: null, cantidad: 1, costoUnitario: 0, categoria: 'materiaPrima', isNew: false });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (editingIndex !== null) return toast.error("Tienes un artículo en edición.");
        if (!proveedor || items.length === 0) return toast.error("Proveedor y al menos un artículo son obligatorios.");
        
        const dataToSave = {
            ...initialData,
            proveedor: proveedor.value,
            fecha, items,
            costoTotal: items.reduce((total, item) => total + (item.costoTotalItem || 0), 0),
            estado: isDirectPurchase ? 'Recibido' : (initialData?.estado || 'Pedido'),
            stockUpdated: !!isDirectPurchase,
        };
        onSave(dataToSave, initialData?.id, isDirectPurchase);
    };

    const handleStartEdit = (index) => {
        const itemToEdit = items[index];
        const productOption = inventarioOptions.find(opt => opt.value === itemToEdit.producto) || { label: itemToEdit.producto, value: itemToEdit.producto };
        setEditingIndex(index);
        setCurrentItem({ 
            producto: productOption, 
            cantidad: itemToEdit.cantidad, 
            costoUnitario: itemToEdit.costoUnitario, 
            categoria: itemToEdit.categoria || 'materiaPrima', 
            isNew: !inventarioOptions.some(opt => opt.value === itemToEdit.producto) 
        });
    };
    const handleUpdateItem = () => {
        if (!currentItem.producto?.value || currentItem.cantidad <= 0 || currentItem.costoUnitario < 0) { return toast.error("Datos incompletos."); }
        const updatedItems = items.map((item, index) => {
            if (index === editingIndex) {
                return { 
                    producto: currentItem.producto.value, 
                    cantidad: currentItem.cantidad, 
                    costoUnitario: currentItem.costoUnitario,
                    costoTotalItem: currentItem.cantidad * currentItem.costoUnitario,
                    categoria: currentItem.categoria
                };
            }
            return item;
        });
        setItems(updatedItems);
        setEditingIndex(null);
        setCurrentItem({ producto: null, cantidad: 1, costoUnitario: 0, categoria: 'materiaPrima', isNew: false });
        toast.success('¡Artículo actualizado!');
    };
    const handleCancelEdit = () => {
        setEditingIndex(null);
        setCurrentItem({ producto: null, cantidad: 1, costoUnitario: 0, categoria: 'materiaPrima', isNew: false });
    };
    const handleRemoveItem = (indexToRemove) => {
        if(indexToRemove === editingIndex) handleCancelEdit();
        setItems(items.filter((_, index) => index !== indexToRemove));
    };
    const costoTotalCompra = items.reduce((total, item) => total + (item.costoTotalItem || 0), 0);
    const inputStyle = "w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#18BC9C] focus:border-[#18BC9C]";
    const labelStyle = "block text-sm font-medium text-gray-700";
    const selectStyles = {
        control: (base) => ({ ...base, border: '1px solid #D1D5DB', boxShadow: 'none', '&:hover': { borderColor: '#18BC9C' }, minHeight: '42px' }),
        option: (styles, { isFocused }) => ({ ...styles, backgroundColor: isFocused ? '#18BC9C' : null, color: isFocused ? 'white' : 'black' }),
    };

    return (
        <form onSubmit={handleSubmit} className="bg-white p-6 sm:p-8 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6">{initialData?.id ? 'Editar Orden' : (isDirectPurchase ? 'Registrar Compra Directa' : 'Crear Orden')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-6 border-b">
                <div><label className={labelStyle}>Proveedor</label><CreatableSelect isClearable options={proveedoresOptions} value={proveedor} onChange={setProveedor} placeholder="Selecciona o crea..." formatCreateLabel={userInput => `Añadir "${userInput}"`} styles={selectStyles}/></div>
                <div><label className={labelStyle}>Fecha</label><DatePicker selected={fecha} onChange={setFecha} dateFormat="dd/MM/yyyy" className={inputStyle} /></div>
            </div>
            <div className="pt-6">
                <h4 className="font-bold text-lg mb-2">{editingIndex !== null ? 'Editando Artículo' : 'Añadir Artículos'}</h4>
                {/* Chad: Layout de inputs adaptable */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                    <div className="lg:col-span-2"><label className={labelStyle}>Producto</label><CreatableSelect isClearable options={inventarioOptions} value={currentItem.producto} onChange={handleProductChange} placeholder="Busca o crea..." formatCreateLabel={userInput => `Añadir "${userInput}"`} styles={selectStyles}/></div>
                    {currentItem.isNew && <div><label className={labelStyle}>Categoría</label><select value={currentItem.categoria} onChange={e => setCurrentItem({...currentItem, categoria: e.target.value})} className={inputStyle}><option value="materiaPrima">Materia Prima</option><option value="accesorios">Accesorio</option></select></div>}
                    <div><label className={labelStyle}>Cantidad</label><input type="number" value={currentItem.cantidad} onChange={e => setCurrentItem({...currentItem, cantidad: Number(e.target.value)})} min="1" className={inputStyle} /></div>
                    <div><label className={labelStyle}>Costo/Unidad</label><input type="number" value={currentItem.costoUnitario} onChange={e => setCurrentItem({...currentItem, costoUnitario: Number(e.target.value)})} step="0.01" min="0" className={inputStyle} /></div>
                </div>
                <div className="mt-3 flex flex-col sm:flex-row items-center gap-4">
                    {editingIndex !== null ? (<><button type="button" onClick={handleUpdateItem} className="w-full sm:w-auto bg-teal-500 text-white font-semibold py-2 px-4 rounded-lg hover:bg-teal-600 flex items-center justify-center gap-2"><FaEdit /> Actualizar</button><button type="button" onClick={handleCancelEdit} className="w-full sm:w-auto bg-gray-200 text-gray-800 font-semibold py-2 px-4 rounded-lg hover:bg-gray-300">Cancelar</button></>) : (<button type="button" onClick={handleAddItem} className="w-full sm:w-auto bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg hover:bg-gray-800 flex items-center justify-center gap-2"><FaPlus /> Añadir</button>)}
                </div>
            </div>
            <div className="mt-6">
                <h4 className="font-bold text-lg mb-2">Artículos en Orden</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto border p-2 rounded-lg bg-gray-50">
                    {items.length > 0 ? items.map((item, index) => (
                        <div key={index} className={`flex flex-col sm:flex-row justify-between sm:items-center bg-white p-2 rounded shadow-sm ${editingIndex === index ? 'ring-2 ring-teal-500' : ''}`}>
                            <div><p className="font-semibold">{item.producto}</p><p className="text-sm text-gray-600">{item.cantidad} x {(item.costoUnitario || 0).toLocaleString('es-ES', {style:'currency', currency:'EUR'})} = <span className="font-bold">{(item.costoTotalItem || 0).toLocaleString('es-ES', {style:'currency', currency:'EUR'})}</span></p></div>
                            <div className="flex items-center gap-2 self-end sm:self-center"><button type="button" onClick={() => handleStartEdit(index)} className="text-gray-400 hover:text-blue-600 p-1"><FaEdit /></button><button type="button" onClick={() => handleRemoveItem(index)} className="text-gray-400 hover:text-red-600 p-1"><FaTrash /></button></div>
                        </div>
                    )) : <p className="text-center text-gray-500 py-4">Añade artículos.</p>}
                </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row justify-between items-center pt-6 border-t mt-6 gap-4">
                <div><p className="text-gray-600">Total:</p><p className="text-2xl font-bold text-gray-800">{costoTotalCompra.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</p></div>
                <div className="flex w-full sm:w-auto space-x-4"><button type="button" onClick={onCancel} className="flex-1 sm:flex-none bg-gray-200 text-gray-800 font-bold h-11 px-6 rounded-lg hover:bg-gray-300">Cancelar</button><button type="submit" className="flex-1 sm:flex-none bg-[#18BC9C] text-white font-bold h-11 px-6 rounded-lg shadow-md hover:bg-[#16a085]">Guardar</button></div>
            </div>
        </form>
    );
}

// --- Chad: Lista de compras adaptada a tarjetas ---
function ComprasList({ compras, tipo, onEdit, onDelete, onMarkAsReceived, onStatusChange, onRevert }) {
    const statusOptions = ["Pedido", "Enviado", "Cancelada"];
    const getStatusChipColor = (status) => {
        switch (status) {
            case 'Recibido': return 'bg-green-100 text-green-800';
            case 'Devuelto': return 'bg-orange-100 text-orange-800';
            case 'Cancelada': return 'bg-red-100 text-red-800';
            case 'Enviado': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };
    return (
        <div className="space-y-4">
            {compras.length > 0 ? (compras.map(compra => (
                <div key={compra.id} className="bg-white p-4 rounded-lg shadow-md border-l-4 border-teal-500">
                    <div className="flex flex-col sm:flex-row justify-between sm:items-start gap-2">
                        <div>
                            <p className="font-bold text-lg text-gray-800">{compra.proveedor}</p>
                            <p className="text-sm text-gray-500">{compra.items?.length || 0} artículo(s)</p>
                        </div>
                        <div className="text-left sm:text-right">
                            <p className="text-sm text-gray-500">Total</p>
                            <p className="font-semibold text-lg">{compra.costoTotal?.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) || '€0.00'}</p>
                        </div>
                    </div>
                    <div className="mt-4 pt-4 border-t flex flex-col md:flex-row justify-between items-center gap-4">
                         <div className="w-full md:w-auto">
                            <label className="text-sm font-medium text-gray-600 mb-1 block">Estado</label>
                            {tipo === 'orden' ? (
                                compra.estado === 'Devuelto' ? (
                                    <select value={compra.estado} onChange={(e) => onStatusChange(compra, e.target.value)} className={`w-full p-2 border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 ${getStatusChipColor(compra.estado)}`}>
                                        <option value="Devuelto">Devuelto</option>
                                        <option value="Pedido">Re-procesar</option>
                                        <option value="Cancelada">Cancelar</option>
                                    </select>
                                ) : (
                                    <select value={compra.estado} onChange={(e) => onStatusChange(compra, e.target.value)} className="w-full p-2 border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-teal-500">
                                        {statusOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                    </select>
                                )
                            ) : (
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getStatusChipColor(compra.estado)}`}>{compra.estado}</span>
                            )}
                        </div>
                        <div className="flex items-center gap-4 self-end md:self-center">
                            {tipo === 'orden' && (
                                <>
                                    <PDFDownloadLink document={<OrdenCompraPDF orden={compra} />} fileName={`Orden-Compra-${compra.id.substring(0,4)}.pdf`}>
                                        {({ loading }) => loading ? '...' : (<button className="text-gray-500 hover:text-red-600" title="Generar PDF"><FaFilePdf size={18}/></button>)}
                                    </PDFDownloadLink>
                                    {compra.estado === 'Pedido' && (<button onClick={() => onMarkAsReceived(compra)} className="text-gray-500 hover:text-teal-600" title="Marcar como Recibida"><FaTruckLoading size={20}/></button>)}
                                </>
                            )}
                            {tipo === 'recibida' && (<button onClick={() => onRevert(compra)} className="text-gray-500 hover:text-orange-600" title="Revertir Compra"><FaUndo size={18}/></button>)}
                            <button onClick={() => onEdit(compra)} className="text-gray-500 hover:text-blue-600" title="Editar"><FaEdit size={18}/></button>
                            <button onClick={() => onDelete(compra)} className="text-gray-500 hover:text-red-600" title="Eliminar"><FaTrash size={16}/></button>
                        </div>
                    </div>
                </div>
            ))) : <p className="text-center py-8 text-gray-500">No hay {tipo === 'orden' ? 'órdenes' : 'compras'} para mostrar.</p>}
        </div>
    );
}

// --- El resto de componentes (ListaPrincipal, Requerimientos) adaptados ---
function ListaPrincipal({ compras, activeTab, onTabChange, onEdit, onDelete, onMarkAsReceived, onStatusChange, onDirectPurchase, onRevert }) {
    const ordenes = compras.filter(c => c.estado !== 'Recibido');
    const recibidas = compras.filter(c => c.estado === 'Recibido');
    return (
        <div>
            <div className="flex justify-start mb-4 border-b">
                <button onClick={() => onTabChange('ordenes')} className={`py-2 px-4 text-sm font-semibold ${activeTab === 'ordenes' ? 'border-b-2 border-teal-500 text-teal-600' : 'text-gray-500'}`}>Órdenes ({ordenes.length})</button>
                <button onClick={() => onTabChange('recibidas')} className={`py-2 px-4 text-sm font-semibold ${activeTab === 'recibidas' ? 'border-b-2 border-teal-500 text-teal-600' : 'text-gray-500'}`}>Recibidas ({recibidas.length})</button>
            </div>
            <div className="mb-8 flex flex-col sm:flex-row items-center gap-4">
                <button onClick={() => onEdit()} className="w-full sm:w-auto bg-[#18BC9C] text-white font-bold h-11 px-6 rounded-lg shadow-md hover:bg-[#16a085] flex items-center justify-center gap-2"><FaPlus /> Crear Orden</button>
                <button onClick={onDirectPurchase} className="w-full sm:w-auto bg-orange-500 text-white font-bold h-11 px-6 rounded-lg shadow-md hover:bg-orange-600 flex items-center justify-center gap-2"><FaShoppingCart /> Compra Directa</button>
            </div>
            {activeTab === 'ordenes' && <ComprasList compras={ordenes} tipo="orden" onEdit={onEdit} onDelete={onDelete} onMarkAsReceived={onMarkAsReceived} onStatusChange={onStatusChange} />}
            {activeTab === 'recibidas' && <ComprasList compras={recibidas} tipo="recibida" onEdit={onEdit} onDelete={onDelete} onRevert={onRevert} />}
        </div>
    );
}

function RequerimientosConsolidadosList({ requerimientos, onProcess }) {
    return (
        <div className="space-y-4">
            {requerimientos.length > 0 ? requerimientos.map(req => (
                <div key={req.id} className="bg-white p-4 rounded-lg shadow-md border-l-4 border-blue-500">
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                        <div>
                            <p className="font-bold text-lg text-gray-800">Req. de {req.departamento}</p>
                            <p className="text-sm text-gray-500">Creado: {req.fechaCreacion?.toDate().toLocaleDateString('es-ES')}</p>
                            <ul className="list-disc list-inside mt-2 text-gray-700">{req.items.map((item, index) => (<li key={index}>{item.nombre} - <span className="font-semibold">Cant: {item.cantidad}</span></li>))}</ul>
                        </div>
                        <button onClick={() => onProcess(req)} className="w-full sm:w-auto bg-teal-500 text-white font-semibold py-2 px-4 rounded-lg hover:bg-teal-600 flex items-center justify-center gap-2 transition-colors duration-200 shrink-0"><FaShoppingCart /> Procesar</button>
                    </div>
                </div>
            )) : <p className="text-center text-gray-500 py-10">No hay requerimientos pendientes.</p>}
        </div>
    );
}

// --- VISTA PRINCIPAL (COMPONENTES UNIDOS) ---
function ComprasView() {
    const [mainView, setMainView] = useState('gestion');
    const [subView, setSubView] = useState('requerimientos');
    const [compras, setCompras] = useState([]);
    const [requerimientos, setRequerimientos] = useState([]);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [compraActual, setCompraActual] = useState(null);
    const [isDirectPurchase, setIsDirectPurchase] = useState(false);
    const [proveedores, setProveedores] = useState([]);
    const [inventarioConsolidado, setInventarioConsolidado] = useState([]);

    useEffect(() => {
        const comprasQuery = query(collection(db, 'compras'), orderBy('createdAt', 'desc'));
        const unsubscribeCompras = onSnapshot(comprasQuery, (snapshot) => setCompras(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
        const reqQuery = query(collection(db, 'requerimientosConsolidados'), orderBy('fechaCreacion', 'desc'));
        const unsubscribeReqs = onSnapshot(reqQuery, (snapshot) => setRequerimientos(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
        const proveedoresQuery = query(collection(db, 'proveedores'), orderBy('name'));
        const unsubscribeProveedores = onSnapshot(proveedoresQuery, (snapshot) => setProveedores(snapshot.docs.map(doc => ({ value: doc.data().name, label: doc.data().name }))));
        const unsubMateriaPrima = onSnapshot(query(collection(db, 'materiaPrima')), (snapshot) => {
            const materiaPrimaItems = snapshot.docs.map(doc => ({ value: doc.data().nombre, label: `${doc.data().nombre} (M. Prima)`, categoria: 'materiaPrima' }));
            setInventarioConsolidado(prev => [...materiaPrimaItems, ...prev.filter(p => p.categoria !== 'materiaPrima')]);
        });
        const unsubAccesorios = onSnapshot(query(collection(db, 'accesorios')), (snapshot) => {
            const accesoriosItems = snapshot.docs.map(doc => ({ value: doc.data().nombre, label: `${doc.data().nombre} (Accesorio)`, categoria: 'accesorios' }));
            setInventarioConsolidado(prev => [...accesoriosItems, ...prev.filter(p => p.categoria !== 'accesorios')]);
        });
        return () => { unsubscribeCompras(); unsubscribeReqs(); unsubscribeProveedores(); unsubMateriaPrima(); unsubAccesorios(); };
    }, []);
    
    const handleUpdateStock = async (items, proveedor) => {
        const batch = writeBatch(db);
        for (const item of items) {
            const collectionName = item.categoria || 'materiaPrima';
            const q = query(collection(db, collectionName), where("nombre", "==", item.producto));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                const docRef = querySnapshot.docs[0].ref;
                const newQty = (querySnapshot.docs[0].data().cantidad || 0) + item.cantidad;
                batch.update(docRef, { cantidad: newQty });
            } else {
                batch.set(doc(collection(db, collectionName)), { nombre: item.producto, cantidad: item.cantidad, proveedor: proveedor, precio: item.costoUnitario || 0 });
            }
        }
        await batch.commit();
    };

    const handleSave = async (formData, compraId, isDirect) => {
        const toastId = toast.loading('Guardando...');
        try {
            const proveedorExists = proveedores.some(p => p.value === formData.proveedor);
            if (!proveedorExists) {
                await setDoc(doc(db, 'proveedores', formData.proveedor), { name: formData.proveedor, createdAt: serverTimestamp() });
                toast('Nuevo proveedor guardado.', { icon: '👏' });
            }
            if (isDirect) {
                await handleUpdateStock(formData.items, formData.proveedor);
                await addDoc(collection(db, "compras"), { ...formData, createdAt: serverTimestamp() });
                toast.success("¡Compra y stock actualizados!", { id: toastId });
            } else {
                const { requerimientoId, ...ordenData } = formData;
                if (compraId) {
                    await updateDoc(doc(db, 'compras', compraId), ordenData);
                } else {
                    await addDoc(collection(db, 'compras'), { ...ordenData, createdAt: serverTimestamp() });
                    if (requerimientoId) await updateDoc(doc(db, "requerimientosConsolidados", requerimientoId), { estado: "Procesado" });
                }
                toast.success("¡Orden de compra guardada!", { id: toastId });
            }
            closeModal();
        } catch (error) { toast.error(`Error: ${error.message}`, { id: toastId }); }
    };
    
    const handleMarkAsReceived = async (orden) => {
        const toastId = toast.loading('Registrando en stock...');
        try {
            await handleUpdateStock(orden.items, orden.proveedor);
            await updateDoc(doc(db, "compras", orden.id), { estado: 'Recibido', stockUpdated: true });
            toast.success("¡Compra recibida y stock actualizado!", { id: toastId });
        } catch (error) { toast.error("No se pudo actualizar el stock.", { id: toastId }); }
    };
    
    const openModal = (compra = null) => { setIsDirectPurchase(false); setCompraActual(compra); setModalIsOpen(true); };
    const closeModal = () => { setCompraActual(null); setModalIsOpen(false); };
    const openDirectPurchaseModal = () => { setIsDirectPurchase(true); setCompraActual(null); setModalIsOpen(true); };
    const handleProcessRequirement = (requerimiento) => {
        const itemsParaOrden = requerimiento.items.map(item => ({ producto: item.nombre, cantidad: item.cantidad, costoUnitario: 0, costoTotalItem: 0 }));
        const nuevaOrden = { proveedor: '', fecha: new Date(), items: itemsParaOrden, estado: 'Pedido', requerimientoId: requerimiento.id };
        setIsDirectPurchase(false); setCompraActual(nuevaOrden); setModalIsOpen(true);
    };
    const handleDelete = async (compra) => {
        if (window.confirm("¿Seguro que quieres eliminar esto?")) {
            const toastId = toast.loading('Eliminando...');
            try {
                if (compra.stockUpdated) {
                    const batch = writeBatch(db);
                    for (const item of compra.items) {
                        const collectionName = item.categoria || 'materiaPrima';
                        const q = query(collection(db, collectionName), where("nombre", "==", item.producto));
                        const querySnapshot = await getDocs(q);
                        if (!querySnapshot.empty) {
                            const docRef = querySnapshot.docs[0].ref;
                            const newQty = (querySnapshot.docs[0].data().cantidad || 0) - item.cantidad;
                            batch.update(docRef, { cantidad: newQty < 0 ? 0 : newQty });
                        }
                    }
                    batch.delete(doc(db, 'compras', compra.id));
                    await batch.commit();
                } else { await deleteDoc(doc(db, 'compras', compra.id)); }
                toast.success("Eliminado correctamente.", { id: toastId });
            } catch (error) { toast.error(`Error: ${error.message}`, { id: toastId }); }
        }
    };
    const handleRevertPurchase = async (compra) => {
        if (window.confirm("¿Revertir esta compra? El stock será descontado.")) {
            const toastId = toast.loading('Revirtiendo...');
            try {
                const batch = writeBatch(db);
                for (const item of compra.items) {
                    const collectionName = item.categoria || 'materiaPrima';
                    const q = query(collection(db, collectionName), where("nombre", "==", item.producto));
                    const querySnapshot = await getDocs(q);
                    if (!querySnapshot.empty) {
                        const docRef = querySnapshot.docs[0].ref;
                        const newQty = (querySnapshot.docs[0].data().cantidad || 0) - item.cantidad;
                        batch.update(docRef, { cantidad: newQty < 0 ? 0 : newQty });
                    }
                }
                batch.update(doc(db, "compras", compra.id), { estado: 'Devuelto', stockUpdated: false });
                await batch.commit();
                toast.success("¡Compra revertida y stock actualizado!", { id: toastId });
            } catch (error) { toast.error("No se pudo revertir la compra.", { id: toastId }); }
        }
    };
    const handleStatusChange = async (compra, nuevoEstado) => {
        if (['Recibido', 'Devuelto'].includes(nuevoEstado) && compra.estado !== 'Devuelto') {
            return toast.error(`Usa el botón correspondiente para esta acción.`);
        }
        if (compra.estado === 'Devuelto' && !['Pedido', 'Cancelada'].includes(nuevoEstado)) {
            return toast.error('Desde "Devuelto" solo puedes re-procesar o cancelar.');
        }
        try {
            await updateDoc(doc(db, 'compras', compra.id), { estado: nuevoEstado });
            toast.success(`Estado actualizado a: ${nuevoEstado}`);
        } catch (error) { toast.error("No se pudo actualizar el estado."); }
    };
    const SubTabView = ({ tabs, activeTab, setActiveTab }) => (
        <div className="mb-6 border-b border-gray-200">
            <nav className="-mb-px flex space-x-2 sm:space-x-6 overflow-x-auto" aria-label="Tabs">
                {tabs.map((tab) => (
                    <button key={tab.name} onClick={() => setActiveTab(tab.id)} className={`${activeTab === tab.id ? 'border-teal-500 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm relative`}>
                        {tab.name}
                        {tab.count > 0 && (<span className="ml-2 absolute top-2 -right-4 flex h-5 w-5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-5 w-5 bg-red-500 text-white text-xs items-center justify-center">{tab.count}</span></span>)}
                    </button>
                ))}
            </nav>
        </div>
    );
    
    return (
        <div className="h-full flex flex-col p-4 md:p-6 gap-6">
            <Toaster position="top-center" />
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Centro de Compras</h1>
                <div className="flex items-center bg-gray-200 rounded-lg p-1 self-stretch sm:self-auto">
                    <button onClick={() => { setMainView('gestion'); setSubView('requerimientos'); }} className={`w-1/2 sm:w-auto px-4 py-2 text-sm rounded-md flex items-center justify-center gap-2 ${mainView === 'gestion' ? 'bg-white shadow text-gray-800 font-semibold' : 'text-gray-600'}`}><FaCashRegister /> Gestión</button>
                    <button onClick={() => { setMainView('solicitud'); setSubView('madera'); }} className={`w-1/2 sm:w-auto px-4 py-2 text-sm rounded-md flex items-center justify-center gap-2 ${mainView === 'solicitud' ? 'bg-white shadow text-gray-800 font-semibold' : 'text-gray-600'}`}><FaTools/> Solicitar</button>
                </div>
            </div>
            
            {mainView === 'gestion' && (
                <div>
                    <SubTabView tabs={[ { id: 'requerimientos', name: 'Req. por Comprar', count: requerimientos.filter(r => r.estado === 'Pendiente de Compra').length }, { id: 'ordenes', name: 'Órdenes' }, { id: 'recibidas', name: 'Recibidas' } ]} activeTab={subView} setActiveTab={setSubView}/>
                    {subView === 'requerimientos' && <RequerimientosConsolidadosList requerimientos={requerimientos.filter(r => r.estado === 'Pendiente de Compra')} onProcess={handleProcessRequirement} />}
                    {(subView === 'ordenes' || subView === 'recibidas') && <ListaPrincipal compras={compras} activeTab={subView} onTabChange={setSubView} onEdit={openModal} onDelete={handleDelete} onMarkAsReceived={handleMarkAsReceived} onStatusChange={handleStatusChange} onDirectPurchase={openDirectPurchaseModal} onRevert={handleRevertPurchase} />}
                </div>
            )}

            {mainView === 'solicitud' && (
                <div>
                    <SubTabView tabs={[ { id: 'madera', name: 'Dpto. Madera' }, { id: 'softplay', name: 'Dpto. Softplay' } ]} activeTab={subView} setActiveTab={setSubView} />
                    {subView === 'madera' && <RequerimientoMadera />}
                    {subView === 'softplay' && <RequerimientoSoftplay />}
                </div>
            )}
            
            <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={customModalStyles}>
                <OrdenCompraForm onSave={handleSave} onCancel={closeModal} initialData={compraActual} isDirectPurchase={isDirectPurchase} proveedoresOptions={proveedores} inventarioOptions={inventarioConsolidado} />
            </Modal>
        </div>
    );
}

export default ComprasView;